import React from 'react';
import { Helmet } from 'react-helmet-async';
import './LegalPages.css';

const LAST_UPDATED = 'February 7, 2026';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Terms of Service | Tmper</title>
        <meta
          name="description"
          content="Terms of Service for Tmper temporary file and image upload hosting. Learn acceptable use, limitations, and disclaimers."
        />
        <link rel="canonical" href="https://tmper.app/terms" />
        <meta property="og:title" content="Terms of Service | Tmper" />
        <meta property="og:description" content="Terms of Service for Tmper temporary uploads." />
        <meta property="og:url" content="https://tmper.app/terms" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: {LAST_UPDATED}</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing and using Tmper ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by these terms, please do not use this service.</p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>Tmper is a free temporary file and image hosting service that allows users to upload files and receive shareable links. All uploaded content is automatically deleted after 1 hour.</p>
        </section>

        <section>
          <h2>3. User Responsibilities</h2>
          <h3>3.1 Acceptable Use</h3>
          <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul>
            <li>Upload content that is illegal, harmful, threatening, abusive, defamatory, or otherwise objectionable</li>
            <li>Upload content that infringes on intellectual property rights of others</li>
            <li>Upload content containing malware, viruses, or other malicious code</li>
            <li>Upload adult content, pornography, or sexually explicit material</li>
            <li>Use the Service to harass, abuse, or harm another person</li>
            <li>Attempt to gain unauthorized access to the Service or its systems</li>
            <li>Use the Service for commercial purposes without permission</li>
          </ul>

          <h3>3.2 Content Responsibility</h3>
          <p>You are solely responsible for the content you upload. You warrant that you own or have the necessary rights to upload and share the content.</p>
        </section>

        <section>
          <h2>4. Service Limitations</h2>
          <h3>4.1 File Size and Type</h3>
          <p>The Service currently supports files up to 100MB per file. We reserve the right to modify these limits at any time.</p>

          <h3>4.2 Temporary Nature</h3>
          <p>All uploaded content is automatically and permanently deleted after 1 hour. We cannot recover deleted content.</p>

          <h3>4.3 Service Availability</h3>
          <p>We strive to maintain high availability but do not guarantee uninterrupted service. The Service may be temporarily unavailable due to maintenance, updates, or technical issues.</p>
        </section>

        <section>
          <h2>5. Prohibited Content</h2>
          <p>The following types of content are strictly prohibited:</p>
          <ul>
            <li>Copyrighted material without permission</li>
            <li>Illegal or criminal content</li>
            <li>Hate speech or discriminatory content</li>
            <li>Adult or sexually explicit content</li>
            <li>Violence or graphic content</li>
            <li>Spam or promotional content</li>
            <li>Personal information of others without consent</li>
          </ul>
        </section>

        <section>
          <h2>6. Privacy and Data Protection</h2>
          <p>Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information. By using the Service, you consent to the collection and use of information as outlined in our Privacy Policy.</p>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <p>The Service and its original content, features, and functionality are owned by Tmper and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
        </section>

        <section>
          <h2>8. Content Removal</h2>
          <p>We reserve the right to remove any content that violates these Terms of Service without prior notice. We may also suspend or terminate user access for violations.</p>
        </section>

        <section>
          <h2>9. Disclaimers</h2>
          <h3>9.1 Service Provided "As Is"</h3>
          <p>The Service is provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied.</p>

          <h3>9.2 No Warranties</h3>
          <p>We disclaim all warranties, including but not limited to:</p>
          <ul>
            <li>Merchantability and fitness for a particular purpose</li>
            <li>Non-infringement of third-party rights</li>
            <li>Continuous, uninterrupted, or error-free operation</li>
            <li>Security of data transmission or storage</li>
          </ul>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>To the fullest extent permitted by applicable law, Tmper shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.</p>
        </section>

        <section>
          <h2>11. Indemnification</h2>
          <p>You agree to defend, indemnify, and hold harmless Tmper and its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including attorney's fees) arising from your use of the Service or violation of these Terms.</p>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>We may terminate or suspend your access to the Service immediately, without prior notice, for any reason whatsoever, including without limitation if you breach the Terms.</p>
        </section>

        <section>
          <h2>13. Changes to Terms</h2>
          <p>We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.</p>
        </section>

        <section>
          <h2>14. Governing Law</h2>
          <p>These Terms shall be interpreted and governed by the laws of the jurisdiction in which Tmper operates, without regard to conflict of law provisions.</p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>If you have any questions about these Terms of Service, please contact us at:</p>
          <p>Email: support@tmper.app</p>
          <p>Website: https://tmper.app</p>
        </section>

        <section>
          <h2>16. Severability</h2>
          <p>If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.</p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
